import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardTitle } from '@/components/shared/card';
import { Button } from '@/components/shared/button';
import { ROUTES } from '@/config/routes';
import PopulationCard from '@/features/LegacyBenchmarksUI/components/PopulationCard';
import PermitsPer1kCard from '@/features/LegacyBenchmarksUI/components/PermitsPer1kCard';
import HousingStockCard from '@/features/LegacyBenchmarksUI/components/HousingStockCard';
import HousingUnitsPer1kCard from '@/features/LegacyBenchmarksUI/components/HousingUnitsPer1kCard';
import RentalVacancyCard from '@/features/LegacyBenchmarksUI/components/RentalVacancyCard';
import RentalBurdenCard from '@/features/LegacyBenchmarksUI/components/RentalBurdenCard';
import PriceToIncomeRatioCard from '@/features/LegacyBenchmarksUI/components/PriceToIncomeRatioCard';
import HousingStockGrowthCard from '@/features/LegacyBenchmarksUI/components/HousingStockGrowthCard';
import HousingTenureCard from '@/features/LegacyBenchmarksUI/components/HousingTenureCard';
import HousingStockCompositionCard from '@/features/LegacyBenchmarksUI/components/HousingStockCompositionCard';
import PeersCard from '@/features/LegacyBenchmarksUI/components/PeersCard';
import { ShareControls } from '@/features/LegacyBenchmarksUI/components/ShareControls';
import { Users } from "lucide-react";
import { cn, formatPlaceName } from "@/lib/utils";
import JurisdictionCombobox from '@/features/LegacyBenchmarksUI/components/JurisdictionCombobox';
import { ViewToggle } from '@/components/layout/ViewToggle';
import { DialGraph } from '@/components/shared/DialGraph';
import CitySnapshotDashboard from '@/features/LegacyBenchmarksUI/components/city-snapshot/CitySnapshotDashboard';
import { useHousingUnitBreakdownData } from '@/hooks/useHousingUnitBreakdownData';
import { useHousingStockGrowthData } from '@/hooks/useHousingStockGrowthData';
import useResponsiveFontSize from '@/hooks/useResponsiveFontSize';
import { updateURLState } from "@/utils/urlParams";
import { usePermitBenchmarkingURLState } from "@/hooks/usePermitBenchmarkingURLState";
import { useBenchmarkingData } from "@/hooks/useBenchmarkingData";
import { usePeerCities } from "@/hooks/usePeerCities";
import { useSoftReload } from "@/hooks/useSoftReload";
import { useDisableScrollRestoration } from '@/hooks/useDisableScrollRestoration';
import { useShareUrl } from '@/hooks/useShareUrl';
import { Place } from '@/types';

type View = 'visualized' | 'snapshot' | 'time';

const PermitBenchmarkingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedPlace: placeFromUrl, peers: peersFromUrl } = usePermitBenchmarkingURLState();
  const isInitialRender = useRef(true);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const isReloadingOnMount = useSoftReload(() => {
    updateURLState(setSearchParams, null, [], undefined);
    window.scrollTo(0, 0);
  });

  // Disable browser's automatic scroll restoration and scroll to top on mount.
  useDisableScrollRestoration();

  // State for place selection (combobox logic moved to JurisdictionCombobox)
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(() => 
    isReloadingOnMount ? null : placeFromUrl
  );

  // ---- Benchmarking data ---- //
  const [currentPlace, setCurrentPlace] = useState<string | null>(null);
  const [currentPlaceFips, setCurrentPlaceFips] = useState<string | null>(null);

  const {
    records: benchmarkingRecords,
    loading: loadingBenchmarkingData,
    error: benchmarkingDataError,
  } = useBenchmarkingData(currentPlace, currentPlaceFips);

  // ---- Peer cities ---- //
  const {
    peerCities,
    loadingPeers,
    peersError,
    addPeer,
    removePeer,
  } = usePeerCities({ selectedPlace, initialPeers: isReloadingOnMount ? [] : peersFromUrl });

  const { data: housingUnitBreakdownData } = useHousingUnitBreakdownData(currentPlace, currentPlaceFips);
  const { data: housingStockGrowthData } = useHousingStockGrowthData(currentPlace, currentPlaceFips);

  // Share URL automatically updates with router location
  const shareUrl = useShareUrl();

  const [view, setView] = useState<'map' | 'peers'>('map');
  const [activeView, setActiveView] = useState<View>('visualized');
  const labelFontSize = useResponsiveFontSize();

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    updateURLState(setSearchParams, selectedPlace, peerCities, undefined);
  }, [selectedPlace, peerCities, setSearchParams]);

  // Keep currentPlace and FIPS in sync with selectedPlace
  useEffect(() => {
    if (selectedPlace && selectedPlace.place_fips) {
      setCurrentPlace(selectedPlace.name);
      setCurrentPlaceFips(selectedPlace.place_fips);
    } else {
      setCurrentPlace(null);
      setCurrentPlaceFips(null);
    }
  }, [selectedPlace]);

  const handleStartBenchmarking = () => {
    navigate(ROUTES.READINESS_ASSESSMENT.ROOT);
  };

  return (
    <>
      <div
        className={cn("relative z-10")}
      >
        <div className="p-8 text-white bg-gradient-to-r from-[#ca0270] to-[#f6b90d]">
          <div className="flex flex-col justify-start items-center text-white">
            <h1 className="text-3xl font-bold mb-4 text-center">
              Permit Benchmarking
            </h1>

            <div className="w-full max-w-2xl mx-auto">
              <label
                htmlFor="place-search"
                className="block text-sm font-medium mb-2 text-gray-200 text-center"
              >
                Select A Jurisdiction to View Benchmarks!
              </label>
              <JurisdictionCombobox
                selectedPlace={selectedPlace}
                scrollTargetRef={resultsContainerRef}
                onSelect={(place: Place) => {
                  setSelectedPlace(place);
                }}
              />
            </div>
          </div>
        </div>
        
        <div ref={resultsContainerRef} className="bg-gray-50">
          <div className="container px-4 py-8 max-w-screen-2xl">
            <div className="mb-4">
              <div className="flex flex-col md:flex-row md:items-center mb-3">
                <CardTitle className="flex items-center">
                  <Users className="mr-2" /> 
                  {currentPlace ? `Peer Jurisdictions for ${formatPlaceName(currentPlace)}` : 'Peer Jurisdictions'}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1 md:mt-0 md:ml-4">
                  {currentPlace 
                    ? `Generated peer list for ${formatPlaceName(currentPlace)}. Click on a peer to remove it. Click the add button to add up to 10 peers.`
                    : 'Select a jurisdiction above to view and compare against its peers.'
                  }
                </p>
              </div>
              <PeersCard
                peers={peerCities}
                isLoading={loadingPeers}
                error={peersError}
                onRemovePeer={removePeer}
                onAddPeer={addPeer}
                selectedPlace={selectedPlace}
              />
            </div>
            
            {currentPlace && (
              <ViewToggle
                selectedCity={currentPlace}
                activeView={activeView}
                onViewChange={setActiveView}
              />
            )}

            {currentPlace ? (
              <>
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 mb-8 ${activeView !== 'visualized' ? 'hidden' : ''}`}>
                  <PopulationCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <PermitsPer1kCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <HousingStockGrowthCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <HousingStockCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <HousingTenureCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <HousingUnitsPer1kCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <PriceToIncomeRatioCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <RentalBurdenCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                  <RentalVacancyCard place={currentPlace} placeFips={currentPlaceFips} peers={peerCities} labelFontSize={labelFontSize} />
                </div>

                <div className={`py-4 ${activeView !== 'snapshot' ? 'hidden' : ''}`}>
                  <CitySnapshotDashboard
                    selectedPlace={selectedPlace}
                    benchmarkingRecords={benchmarkingRecords}
                    housingUnitBreakdownRecords={housingUnitBreakdownData}
                    housingStockGrowthRecords={housingStockGrowthData}
                  />
                </div>

                <div className={`text-center p-8 ${activeView !== 'time' ? 'hidden' : ''}`}>
                  <p>Time series view coming soon.</p>
                </div>
              </>
            ) : (
              <div className="text-center p-16 border-2 border-dashed rounded-lg text-gray-500">
                Select a jurisdiction to view key metrics.
              </div>
            )}

            {selectedPlace && (
              <div className="mb-8 text-center max-w-2xl mx-auto">
                <ShareControls 
                  shareUrl={shareUrl} 
                  title={`Permit Benchmarking for ${formatPlaceName(selectedPlace.name)}`}
                  summary={`I'm exploring housing and permit data for ${formatPlaceName(selectedPlace.name)} and its peers. Check out the dashboard to see how it compares on key metrics!`}
                />
              </div>
            )}

            {selectedPlace && (
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">About Permit Benchmarking</h2>
                <p className="text-gray-600 mb-6">
                  Compare your permitting process performance against peer jurisdictions and identify opportunities for improvement.
                </p>
                <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                  <li>Processing Times</li>
                  <li>Staff Efficiency</li>
                  <li>Customer Satisfaction</li>
                  <li>Digital Channel Adoption</li>
                  <li>Cost Recovery</li>
                  <li>First Contact Resolution</li>
                </ul>
                <p className="text-gray-600 mb-6">
                  Get insights into how your permitting performance compares to similar jurisdictions.
                </p>
                <Button 
                  onClick={handleStartBenchmarking}
                  className="w-full md:w-auto"
                >
                  Start Benchmarking
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PermitBenchmarkingPage; 